import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/Elements/MenuButton';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import { useEffect, useState } from 'react';
import { t } from 'src/lib/language';
import { TextField, Tooltip } from '@material-ui/core';
import { isEmpty, keyBy, sortBy } from 'lodash';
import { getOrganisationBankAccountAction } from 'src/redux/actions/paymentInfoAction';
import { getGatewayListAction } from 'src/redux/actions/gatewayAction';
import { BankAccountModel, BankAccountPayload } from 'src/models/BankAccountModel';
import EditBankAccountForm from 'src/components/Forms/EditBankAccountForm';
import AddBankAccountForm from 'src/components/Forms/AddBankAccountForm';
import Button from '@material-ui/core/Button';
import { Pagination } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  header: {
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1),
    height: '56px',
  },
  title: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.light,
    fontSize: '18px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    height: '4vh',
    gap: '10px',
  },
  scroller: {
    overflowY: 'auto',
    height: '62vh',
    padding: '8px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    height: '4vh',
    gap: '10px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.25)',
  },
  search: {
    height: '6vh',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.25)',
  },
}));

export enum STATE {
  list = 'list',
  edit = 'edit',
  add = 'add',
}

const ManageBankAccounts = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const organisationId = useSelector((state: Store) => state.currentOrganisation.id);
  const bankAccounts = useSelector(
    (state: Store) => state.organisationBankAccounts[organisationId] || [] as any[],
  );
  const gateways = useSelector(
    (state: Store) => state.gateways ?? {},
  );

  const canDeleteBankAccount = (id: number) => {
    const gatewayList = gateways;
    if (isEmpty(gatewayList)) {
      return true;
    }
    return (!(keyBy(gatewayList, 'bank_account_id')[id]));
  };

  useEffect(() => {
    dispatch(getOrganisationBankAccountAction());
    dispatch(getGatewayListAction());
  }, []);

  const lang = useSelector((state: Store) => state.language.language);

  const [currentAccount, setCurrentAccount] = useState({} as BankAccountPayload);
  const [currentState, setCurrentState] = useState(STATE.list);
  const [currentFilter, setCurrentFilter] = useState('');
  const [page, setPage] = useState(1);

  const handleDelete = (e:any, account: BankAccountPayload) => {
    e.preventDefault();
    dispatch(openDialog(
      {
        dialog: DIALOGS.deleteBankAccount,
        payload: { accountId: account.id, contactId: undefined },
      },
    ));
  };

  const handleEdit = (account: BankAccountPayload) => {
    setCurrentAccount(account);
    setCurrentState(STATE.edit);
  };

  const handleAdd = () => {
    setCurrentAccount({} as BankAccountPayload);
    setCurrentState(STATE.add);
  };

  const handlePageChange = (e:any, newPage:number) => {
    setPage(newPage);
  };

  const displayBankAccount =
    (account: BankAccountPayload) => `
      ${account.bank}-${account.branch}-${account.account} (${account.bank_name})
    `;

  const sortBankAccounts = () => sortBy(
    bankAccounts, (account: BankAccountModel) => account.bank_name,
  );

  const filterBankAccounts = (sortedAccounts: BankAccountModel[]) => sortedAccounts
    .filter((account: BankAccountModel) => {
      if (!currentFilter) return true;
      const bankName = account.bank_name || '';
      const bankNameLowercase = bankName.toLowerCase();
      const currentFilterLowercase = currentFilter.toLowerCase();
      return bankNameLowercase.includes(currentFilterLowercase);
    });

  const getItems = (filteredBankAccounts: BankAccountModel[]) => filteredBankAccounts
    .map((account: BankAccountModel) => (
      <div className={classes.item} key={`account-${account.id}`}>
        <span>
          {displayBankAccount(account)}
        </span>
        <Tooltip
          title={t(lang, 'menus.edit_account')}
          placement="top-start"
        >
          <span style={{ marginLeft: 'auto' }}>
            <ActionButton
              variant="outlined"
              category={MENU_BUTTON_CATEGORY.action}
              onClick={() => handleEdit(account)}
            >
              <EditIcon />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip
          title={t(lang, 'menus.delete_account')}
          placement="top-start"
        >
          <span style={{ marginLeft: '8px' }}>
            <ActionButton
              variant="outlined"
              category={MENU_BUTTON_CATEGORY.action}
              onClick={(e) => handleDelete(e, account)}
              disabled={!canDeleteBankAccount(account.id)}
            >
              <DeleteIcon />
            </ActionButton>
          </span>
        </Tooltip>
      </div>
    ));

  const displayItems = () => {
    const sortedBankAccounts = sortBankAccounts();
    const filteredBankAccounts = filterBankAccounts(sortedBankAccounts);
    if (filteredBankAccounts.length <= 15) {
      return (
        <>
          <div>{getItems(filteredBankAccounts)}</div>
        </>
      );
    }

    const count = Math.ceil(filteredBankAccounts.length / 15);
    const start = (page - 1) * 15;
    const paginatedAccounts = filteredBankAccounts.slice(start, start + 15);

    return (
      <>
        <Pagination page={page} count={count} onChange={handlePageChange} size="small" />
        <div style={{ marginBottom: '8px', marginTop: '8px' }}>
          {getItems(paginatedAccounts)}
        </div>
      </>
    );
  };

  const displayEditForm = () => (
    <EditBankAccountForm
      account={currentAccount}
      onClose={() => setCurrentState(STATE.list)}
    />
  );

  const displayAddForm = () => (
    <AddBankAccountForm
      onClose={() => setCurrentState(STATE.list)}
    />
  );

  return (
    <>
      { currentState === STATE.list && (
      <div className={classes.search}>
        <TextField
          margin="dense"
          variant="outlined"
          label="Filter"
          onChange={(e) => setCurrentFilter(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
        >
          <AddIcon /> {t(lang, 'menus.add_bank_account')}
        </Button>
      </div>
      )}
      <div className={classes.scroller}>
        { currentState === STATE.list && displayItems() }
        { currentState === STATE.add && displayAddForm() }
        { currentState === STATE.edit && displayEditForm() }
      </div>
    </>
  );
};

export default ManageBankAccounts;
